// Uploading files to the bucket from the client.
//
// The bytes go straight from the browser to S3, the way they always have. What
// changed is where the authority to write comes from: instead of the browser
// holding an AWS credential, it asks the server for a presigned PUT scoped to
// one key and valid for a few minutes.
//
// That keeps these out of back-end/ entirely. They are not dispatched to and
// they hold no secret, so they live here, on the client side of the line, and
// the folder the server scans is left with nothing binary in it.
//
// XMLHttpRequest rather than fetch, because fetch cannot report upload
// progress. The progress bar and the stall detection both need it.

import * as libCon from '../Constants.js';
import { createUploadURL } from '../back-end/router.js';
import { sleep } from './generalFunctions.js';

// No progress for this long and the upload is treated as dead rather than slow.
const STALL_TIMEOUT_MS = 30000;
const STALL_CHECK_INTERVAL_MS = 5000;

/**
 * Sends one file to S3 using presigned POST credentials.
 *
 * The file goes last in the form: S3 ignores anything that follows it, so a
 * field ordered after it would silently not apply.
 *
 * The request's own Content-Type is left alone. It has to be the multipart
 * type the browser generates, boundary and all; the object's type travels as
 * a form field instead, which is what the policy matches on.
 *
 * Resolves on a 2xx and rejects otherwise, so the retry loop above it sees a
 * failed attempt the same way whether the network died or S3 refused.
 *
 * @param {string} url    the bucket endpoint to post to
 * @param {object} fields the policy fields, sent exactly as handed over
 * @param {File|Blob} file
 * @param {(loaded: number, total: number) => void} onProgress
 */
const postToPresignedURL = (url, fields, file, onProgress) =>
  new Promise((resolve, reject) => {
    const form = new FormData();

    Object.entries(fields).forEach(([name, value]) => form.append(name, value));
    form.append('file', file);

    const xhr = new XMLHttpRequest();

    // Stall detection: S3 can leave a connection open long after it has
    // stopped moving, and without this the upload would sit there until the
    // browser gave up on its own.
    let lastProgressAt = Date.now();
    let lastLoaded = 0;

    const stallCheck = setInterval(() => {
      if (Date.now() - lastProgressAt > STALL_TIMEOUT_MS) {
        clearInterval(stallCheck);
        xhr.abort();
      }
    }, STALL_CHECK_INTERVAL_MS);

    const finish = (settle, value) => {
      clearInterval(stallCheck);
      settle(value);
    };

    xhr.upload.onprogress = (event) => {
      if (event.loaded !== lastLoaded) {
        lastLoaded = event.loaded;
        lastProgressAt = Date.now();
      }

      if (event.lengthComputable) onProgress(event.loaded, event.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return finish(resolve, libCon.OK);

      // S3 answers a rejected policy with a 4xx and an XML body naming the
      // condition that failed, which is worth keeping: without it a size or
      // type refusal is indistinguishable from a dropped connection.
      finish(reject, new Error(`S3 refused the upload with status ${xhr.status}: ${xhr.responseText}`));
    };

    // A network level failure, which is the case worth telling apart from a
    // refusal: it is the one that deserves NETWORK_ERROR.
    xhr.onerror = () => finish(reject, Object.assign(new Error('Network request failed'), { code: 'NetworkingError' }));
    xhr.ontimeout = () => finish(reject, Object.assign(new Error('timeout'), { code: 'TimeoutError' }));
    xhr.onabort = () => finish(reject, Object.assign(new Error('timeout'), { code: 'TimeoutError' }));

    xhr.open('POST', url);
    xhr.send(form);
  });

/**
 * Uploads a file to the bucket, retrying the whole thing on failure.
 *
 * A fresh URL is minted for every attempt. Reusing one would work most of the
 * time and then fail exactly when the retries mattered, once the backoff had
 * outlasted the signature.
 *
 * @param {string} folderName
 * @param {string} fileName
 * @param {File|Blob} file
 * @param {(loaded: number, total: number) => void} onProgress
 * @param {number} totalAttempts
 * @returns libCon.OK, libCon.NETWORK_ERROR or libCon.ERROR
 */
const uploadToBucketWithRetries = async (folderName, fileName, file, onProgress, totalAttempts) => {
  // Progress is reported as a high water mark. Each retry restarts at zero and
  // the bar would otherwise walk backwards, which reads as the upload undoing
  // itself rather than trying again.
  let maxLoaded = 0;
  const reportProgress = (loaded, total) => {
    if (loaded > maxLoaded) maxLoaded = loaded;
    onProgress(maxLoaded, total);
  };

  let attempt = 1;
  let finalError = null;
  let success = false;
  // A refusal the next attempt would only earn again. Set once and the loop
  // stops rather than sitting through the backoff for nothing.
  let fatal = false;

  while (!success && !fatal && attempt <= totalAttempts) {
    try {
      const [status, presigned] = await createUploadURL(folderName, fileName, file.type);

      if (status !== libCon.OK) throw Object.assign(new Error(`Could not get an upload URL: ${status}`), { code: status });

      await postToPresignedURL(presigned.url, presigned.fields, file, reportProgress);
      success = true;
    } catch (error) {
      console.log(`Upload attempt ${attempt} failed:`, error?.message, error?.code);
      finalError = error;
      attempt++;

      // The server turns down a bad key or an unsupported type the same way
      // every time. Everything else -- a dead network, a stalled connection,
      // S3 itself -- is worth another go.
      fatal = error?.code === libCon.ERROR;

      // Exponential backoff between full-upload retries
      if (!fatal && attempt <= totalAttempts) {
        await sleep(2000 * Math.pow(2, attempt - 2)); // 2s, 4s, 8s...
      }
    }
  }

  if (!success) {
    const msg = finalError?.message || '';
    const code = finalError?.code || '';
    if (
      msg === 'Network request failed' ||
      code === 'NetworkingError' ||
      code === 'TimeoutError' ||
      code === libCon.NETWORK_ERROR ||
      msg.includes('timeout')
    ) {
      return libCon.NETWORK_ERROR;
    }
    return libCon.ERROR;
  }

  return libCon.OK;
};

export const uploadImageToBucket = async (
  folderName,
  fileName,
  file,
  onProgress = (loaded, total) => false,
  totalAttempts = 3
) => {
  return await uploadToBucketWithRetries(folderName, fileName, file, onProgress, totalAttempts);
};

const PDF_CONTENT_TYPE = 'application/pdf';

/**
 * Wraps whatever a caller has into a Blob that reports itself as a PDF.
 *
 * pdf-lib's save() answers a Uint8Array, and that is what the lab report export
 * hands over. Raw bytes break both halves of the upload: they have no .type, so
 * the presigned policy cannot be asked for one, and FormData would send them
 * stringified as "37,80,68,70,..." rather than as a file.
 *
 * A Blob that already calls itself a PDF is passed through. One that does not
 * is re-wrapped, since a Blob is itself a valid part, and the type has to match
 * what the policy was signed for.
 */
const asPDF = (file) =>
  file instanceof Blob && file.type === PDF_CONTENT_TYPE ? file : new Blob([file], { type: PDF_CONTENT_TYPE });

export const uploadPDFToBucket = async (
  folderName,
  fileName,
  file,
  onProgress = (loaded, total) => false,
  totalAttempts = 3
) => {
  return await uploadToBucketWithRetries(folderName, fileName, asPDF(file), onProgress, totalAttempts);
};
